import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // [{product, qty}]
  const [open, setOpen] = useState(false);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const found = prev.find((it) => it.product.id === product.id);
      if (found) {
        return prev.map((it) =>
          it.product.id === product.id ? { ...it, qty: it.qty + 1 } : it,
        );
      }
      return [...prev, { product, qty: 1 }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((it) => it.product.id !== productId));
  }, []);

  const updateQty = useCallback((productId, qty) => {
    setItems((prev) =>
      prev
        .map((it) => (it.product.id === productId ? { ...it, qty } : it))
        .filter((it) => it.qty > 0),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((acc, it) => acc + it.qty, 0);
    const subtotal = items.reduce(
      (acc, it) => acc + it.qty * it.product.price,
      0,
    );
    return { count, subtotal };
  }, [items]);

  const value = useMemo(
    () => ({ items, open, setOpen, addItem, removeItem, updateQty, clear, totals }),
    [items, open, addItem, removeItem, updateQty, clear, totals],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
